import type { AttachmentChip } from '@/services/assistant/attachments/attachment-chip'
import { useCallback, useEffect, useRef } from 'react'

/**
 * The chips a send carried come back when that send fails (a 400, a network error, the rate limit), so
 * nobody has to attach them again. `remember` is called with the chips as the message leaves; once the
 * turn has been seen in flight and is over, they are restored if it ended with an error and forgotten
 * otherwise (a reply, a client-tool suspension or Stop all mean the files reached the assistant).
 */
export function useRestoreChipsOnFailure(
  turn: { active: boolean; failed: boolean },
  restore: (chips: readonly AttachmentChip[]) => void,
): (chips: readonly AttachmentChip[]) => void {
  const sentRef = useRef<readonly AttachmentChip[] | null>(null)
  const seenActiveRef = useRef(false)

  useEffect(() => {
    if (!sentRef.current) return

    if (turn.active) {
      seenActiveRef.current = true

      return
    }

    // The send has not reached the machine yet: wait for it to go in flight.
    if (!seenActiveRef.current) return

    const sent = sentRef.current
    sentRef.current = null
    seenActiveRef.current = false
    if (turn.failed) restore(sent)
  }, [turn.active, turn.failed, restore])

  return useCallback((chips: readonly AttachmentChip[]) => {
    sentRef.current = chips.filter(c => c.status === 'ready')
    seenActiveRef.current = false
  }, [])
}
